<template>
  <div class="page-container home-editor">
    <div class="page-title-container d-flex flex-column flex-md-row justify-content-between align-items-md-center">
      <div>
        <h1 class="page-title">Portada de la tienda</h1>
        <p class="page-subtitle mb-0">Configura las secciones que se muestran en la home</p>
      </div>
      <button type="button" class="button button-primary mt-3 mt-md-0" @click="showTypePicker = true"
        :disabled="sections.length >= maxSections">
        + Agregar sección
      </button>
    </div>

    <div class="sections-card">
      <p class="empty-state mb-0" v-if="!loading && !sections.length">
        Aún no hay secciones. Mientras tanto, la tienda muestra la portada de siempre.
      </p>

      <!-- Arrastrar por el icono para cambiar el orden -->
      <draggable v-model="sections" handle=".drag-handle" @end="onReorder" v-else>
        <div class="section-row d-flex align-items-center" v-for="(section, index) in sections" :key="section.id"
          :class="{ hidden: !section.isVisible }">
          <i class="ri-draggable drag-handle" title="Arrastrar para ordenar"></i>
          <span class="section-position">{{ index + 1 }}</span>

          <span class="section-type d-flex align-items-center">
            <i :class="typeOf(section).icon"></i>
            <span class="ml-2">{{ typeOf(section).label }}</span>
          </span>

          <div class="section-info flex-grow-1">
            <p class="section-title mb-0">{{ section.title || "Sin título" }}</p>
            <p class="section-summary mb-0">{{ summary(section) }}</p>
          </div>

          <div class="section-actions d-flex align-items-center">
            <button type="button" class="icon-button" :title="section.isVisible ? 'Ocultar' : 'Mostrar'"
              @click="toggleVisibility(section)">
              <i :class="section.isVisible ? 'ri-eye-line' : 'ri-eye-off-line'"></i>
            </button>
            <button type="button" class="icon-button" title="Editar" @click="openEditor(section)">
              <i class="ri-pencil-line"></i>
            </button>
            <button type="button" class="icon-button text-danger" title="Eliminar" @click="openDelete(section)">
              <i class="ri-delete-bin-line"></i>
            </button>
          </div>
        </div>
      </draggable>
    </div>

    <!-- Elegir el tipo de la sección nueva -->
    <b-modal v-model="showTypePicker" title="Agregar sección" size="lg" centered hide-footer>
      <div class="type-grid">
        <button type="button" class="type-card" v-for="type in sectionTypes" :key="type.key"
          @click="createSection(type.key)">
          <i :class="type.icon"></i>
          <span class="type-label">{{ type.label }}</span>
          <span class="type-description">{{ type.description }}</span>
        </button>
      </div>
    </b-modal>

    <HomeSectionEditor :show="editor.show" :section-id="editor.sectionId" :initial-type="editor.type"
      @hide="editor.show = false" @saved="loadData()" />

    <ConfirmationModal :show="confirmationModal.show" @hide="closeConfirmationModal" :data="confirmationModal"
      @confirm="confirmModal" />
  </div>
</template>

<script>
import { Alert, ConfirmationModal, ConfirmationModalMixin } from "@wizepe/wize-admin";
import draggable from "vuedraggable";

import { HomeSectionService } from "@/core/services";
import { Constants as LocalConstants } from "@/core/utils";

import HomeSectionEditor from "../Components/HomeSectionEditor.vue";

const MAX_SECTIONS = 20;

export default {
  name: "HomeEditor",
  components: {
    ConfirmationModal,
    HomeSectionEditor,
    draggable,
  },
  mixins: [ConfirmationModalMixin],
  data() {
    return {
      loading: true,
      sections: [],
      maxSections: MAX_SECTIONS,
      sectionTypes: Object.values(LocalConstants.HomeSectionTypes),
      showTypePicker: false,
      editor: { show: false, sectionId: null, type: null },
      selectedSection: null,
    };
  },
  methods: {
    async loadData() {
      try {
        this.loading = true;
        this.$store.dispatch("app/loading", true);
        const response = await HomeSectionService.list();
        this.sections = response.payload || [];
      } catch (ex) {
        console.error(ex);
        Alert.error(ex.errors ? ex.errors.message : ex);
      } finally {
        this.loading = false;
        this.$store.dispatch("app/loading", false);
      }
    },
    typeOf(section) {
      return LocalConstants.HomeSectionTypes[section.type] || { label: section.type, icon: "ri-question-line" };
    },
    // Resumen de la configuración para la fila
    summary(section) {
      const parts = [];
      const items = section.items || [];
      const mode = LocalConstants.HomeDisplayModes[section.displayMode];

      switch (section.type) {
        case "BANNERS":
          parts.push(`${items.length} ${items.length === 1 ? "banner" : "banners"}`);
          break;
        case "PRODUCTS": {
          const source = LocalConstants.HomeProductSources[section.source];
          parts.push(source ? source.label : "Manual");
          parts.push(`${section.source === "MANUAL" ? items.length : section.quantity || 8} productos`);
          break;
        }
        case "CATEGORIES":
          parts.push(`${items.length} categorías`);
          break;
        case "BRANDS":
          parts.push(`${items.length} marcas`);
          break;
        case "FREE_TEXT":
          parts.push("Contenido HTML");
          break;
        case "COUPON_BAR":
          parts.push(section.subtitle || "Barra destacada");
          break;
        case "SEPARATOR":
          parts.push("Espacio entre secciones");
          break;
      }

      if (mode) parts.push(mode.label);
      if (section.columns) parts.push(`${section.columns} ${section.columns === 1 ? "columna" : "columnas"}`);
      if (!section.isVisible) parts.push("Oculta");
      return parts.join(" · ");
    },
    createSection(type) {
      this.showTypePicker = false;
      this.editor = { show: true, sectionId: null, type };
    },
    openEditor(section) {
      this.editor = { show: true, sectionId: section.id, type: section.type };
    },
    async onReorder() {
      try {
        const response = await HomeSectionService.reorder(this.sections.map((section) => section.id));
        this.sections = response.payload || this.sections;
      } catch (ex) {
        console.error(ex);
        Alert.error(ex.errors ? ex.errors.message : ex);
        await this.loadData();
      }
    },
    async toggleVisibility(section) {
      try {
        const response = await HomeSectionService.toggleVisibility(section.id, !section.isVisible);
        const index = this.sections.findIndex((item) => item.id === section.id);
        if (index !== -1) this.$set(this.sections, index, response.payload);
      } catch (ex) {
        console.error(ex);
        Alert.error(ex.errors ? ex.errors.message : ex);
      }
    },
    openDelete(section) {
      this.selectedSection = section;
      this.openConfirmationModal({
        title: "Eliminar sección",
        description: "¿Está seguro que desea eliminar esta sección de la portada?",
        acceptText: "Borrar",
        action: "delete",
      });
    },
    confirmModal() {
      this.closeConfirmationModal();
      if (this.confirmationModal.action === "delete") this.deleteSection();
    },
    async deleteSection() {
      try {
        this.$store.dispatch("app/loading", true);
        await HomeSectionService.delete(this.selectedSection.id);
        Alert.success("Sección eliminada");
        await this.loadData();
      } catch (ex) {
        console.error(ex);
        Alert.error(ex.errors ? ex.errors.message : ex);
      } finally {
        this.$store.dispatch("app/loading", false);
      }
    },
  },
  async mounted() {
    await this.loadData();
  },
};
</script>

<style lang="stylus" scoped>
@import "../Styles/home-editor.styl"
</style>
